import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Eye, Edit, Trash2, RotateCcw, Loader2 } from "lucide-react"; // Add Loader2

import showToast from "../utils/toastNotifications";

import useForm from "../hooks/useForm";
import { useThunk } from "../hooks/useThunk";

import {
  fetchAllUsers,
  deleteUser,
  restoreUser,
} from "../store/thunks/userThunks";

import TruncateWithTooltip from "../components/Common/TruncateWithTooltip";
import Loader from "../components/Common/Loader";

const ViewUsers = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const [users, setUsers] = useState([]);
  const [filteredUsers, setFilteredUsers] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalUsers, setTotalUsers] = useState(0);
  const [getUsers, isLoading] = useThunk(fetchAllUsers);

  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [doDeleteUser] = useThunk(deleteUser);
  const [doRestoreUser] = useThunk(restoreUser);
  const rowsPerPage = 10;

  const { formData, handleInputChange, handleBlur } = useForm(
    { searchTerm: "", selectedRole: "" },
    { searchTerm: false, selectedRole: false }
  );

  const [searchFilters, setSearchFilters] = useState({
    searchTerm: "",
    selectedRole: "",
  });

  // Add loading states for delete/restore
  const [deletingUserId, setDeletingUserId] = useState(null);
  const [restoringUserId, setRestoringUserId] = useState(null);

  const totalPages = Math.ceil(totalUsers / rowsPerPage);
  const paginatedUsers = users;

  const loadUsers = useCallback(async () => {
    const resultAction = await getUsers({
      page: currentPage,
      limit: rowsPerPage,
      search: searchFilters.searchTerm,
      role: searchFilters.selectedRole,
    });

    if (resultAction.success) {
      const apiResponse = resultAction.response;
      if (apiResponse && apiResponse.data) {
        const responseData = apiResponse.data;
        let fetchedUsers;
        let total;

        if (Array.isArray(responseData)) {
          fetchedUsers = responseData;
          total = responseData.length;
        } else {
          fetchedUsers = responseData.users || [];
          total = responseData.total || responseData.totalCount || 0;
        }

        if (Array.isArray(fetchedUsers)) {
          const transformed = fetchedUsers.map((u, i) => ({
            id: u.id,
            name: u.firstName + " " + u.lastName,
            role: u.roles,
            email: u.email,
            registered: u.created_at ? u.created_at.split(" ")[0] : "N/A",
            status: u.deleted_at === null ? "Active" : "Inactive",
          }));
          setUsers(transformed);
          setFilteredUsers(transformed);
          setTotalUsers(total);
        } else {
          showToast(
            "error",
            "Unexpected data format received while fetching users."
          );
        }
      } else {
        showToast(
          "error",
          "Unexpected data format received while fetching users."
        );
      }
    } else {
      showToast("error", "Failed to fetch users");
    }
  }, [getUsers, currentPage, rowsPerPage, searchFilters]);

  useEffect(() => {
    loadUsers();
  }, [loadUsers]);

  // Reset to first page when search params change
  useEffect(() => {
    if (searchParams.searchTerm !== "" || searchParams.selectedRole !== "") {
      if (currentPage !== 1) {
        setCurrentPage(1);
      }
    }
  }, [searchParams.searchTerm, searchParams.selectedRole]);

  const handleSearch = () => {
    setSearchParams({
      searchTerm: formData.searchTerm,
      selectedRole: formData.selectedRole,
    });
    setSearchFilters({
      searchTerm: formData.searchTerm,
      selectedRole: formData.selectedRole,
    });
    setCurrentPage(1);
  };

  const handleView = (userId) => {
    navigate(`/admin/create-user?mode=view&id=${userId}`);
  };

  const handleEdit = (userId) => {
    navigate(`/admin/create-user?mode=edit&id=${userId}`);
  };

  // Update delete/restore handlers to set loading state
  const handleDelete = async (userId) => {
    setDeletingUserId(userId);
    const result = await doDeleteUser(userId);
    if (result.success) {
      showToast("success", "User deleted successfully!");
      setUsers((prev) =>
        prev.map((u) => (u.id === userId ? { ...u, status: "Inactive" } : u))
      );
    } else {
      showToast("error", "Failed to delete user.");
    }
    setDeletingUserId(null);
  };

  const handleRestore = async (userId) => {
    setRestoringUserId(userId);
    const result = await doRestoreUser(userId);
    if (result.success) {
      showToast("success", "User restored successfully!");
      setUsers((prev) =>
        prev.map((u) => (u.id === userId ? { ...u, status: "Active" } : u))
      );
    } else {
      showToast("error", "Failed to restore user.");
    }
    setRestoringUserId(null);
  };

  if (isLoading) {
    return (
      <div className="md:mt-[50px] md:p-[20px] lg:p-[50px] lg:mx-[100px] bg-[var(--primary-bg-color)] md:rounded-[15px] shadow-[8px_8px_30px_rgba(0,0,0,0.1)] font-inter">
        <Loader />
      </div>
    );
  }

  const goToPage = (page) => {
    if (page < 1) page = 1;
    else if (page > totalPages) page = totalPages;

    setCurrentPage(page);
  };

  const toggleDropdown = () => {
    setDropdownOpen(!dropdownOpen);
  };

  const handleSelectRole = (role) => {
    handleInputChange("selectedRole", role);
    setDropdownOpen(false);
  };

  return (
    <div className="md:mt-[50px] md:p-[20px] lg:p-[50px] lg:mx-[100px] bg-[var(--primary-bg-color)] md:rounded-[15px] shadow-[8px_8px_30px_rgba(0,0,0,0.1)] font-inter">
      {/* Search + Filter */}
      <div className="pt-10 sm:p-6 mx-2 md:mx-0 rounded-xl mb-6 md:border ">
        <div className="flex flex-col sm:flex-row gap-4 items-center ">
          <input
            type="text"
            name="searchTerm"
            placeholder="Search by name or ID..."
            value={formData.searchTerm}
            onChange={(e) => handleInputChange("searchTerm", e.target.value)}
            onBlur={(e) => handleBlur("searchTerm", e.target.value)}
            className="w-full md:w-1/2 lg:w-2/3 h-[45px] border rounded-md px-4 py-2"
          />
          <div className="relative w-full md:w-1/3 lg:w-1/4">
            <button
              onClick={toggleDropdown}
              className="w-full h-[45px] border text-[var(--text-gray-color)] rounded-md px-4 py-2 appearance-none relative bg-white flex items-center justify-between"
            >
              <span>{formData.selectedRole || "Filter by User Role"}</span>
              <span
                className="absolute right-4 pointer-events-none"
                style={{
                  backgroundImage: `url("data:image/svg+xml,%3Csvg fill='none' stroke='currentColor' stroke-width='2' viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                  backgroundRepeat: "no-repeat",
                  backgroundPosition: "center",
                  backgroundSize: "1rem",
                  width: "1rem",
                  height: "1rem",
                }}
              ></span>
            </button>
            {dropdownOpen && (
              <span className="absolute top-full left-0 w-full bg-[var(--primary-bg-color)] text-[var(--text-gray-color)] border rounded-md shadow-md z-10">
                <div
                  className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                  onClick={() => handleSelectRole("")}
                >
                  Filter by User Role
                </div>
                <div
                  className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                  onClick={() => handleSelectRole("Family Member")}
                >
                  Family Member
                </div>
                <div
                  className="px-4 py-2 hover:bg-gray-100 cursor-pointer"
                  onClick={() => handleSelectRole("Staff")}
                >
                  Staff
                </div>
              </span>
            )}
          </div>
          <button
            onClick={handleSearch}
            className="w-full sm:w-[100px] h-[45px] bg-[var(--button-dark-green-color)] text-[var(--primary-text-color)] rounded-md"
          >
            SEARCH
          </button>
        </div>
      </div>

      {/* Desktop Table */}
      <div className="overflow-x-auto bg-[var(--primary-bg-color)] rounded-xl shadow-md hidden md:block ">
        <table className="w-full text-left">
          <thead>
            <tr className="bg-gradient-to-b from-[#E5F0FF5C] to-[#2B7C864D] text-sm text-[var(--text-green-color)]">
              <th className="py-3 px-4 whitespace-nowrap">User ID</th>
              <th className="py-3 px-4 whitespace-nowrap">Full Name</th>
              <th className="py-3 px-4 whitespace-nowrap">Role</th>
              <th className="py-3 px-4 whitespace-nowrap">Email</th>
              <th className="py-3 px-4 whitespace-nowrap">Date Registered</th>
              <th className="py-3 px-4 whitespace-nowrap">Status</th>
              <th className="py-3 px-4 whitespace-nowrap">Action</th>
            </tr>
          </thead>
          <tbody>
            {paginatedUsers.length > 0 ? (
              paginatedUsers.map((user, idx) => (
                <tr
                  key={idx}
                  className="border-t text-[var(--text-dark-gray-color)] text-[14px]"
                >
                  <td className="py-3 px-4 whitespace-nowrap">{user.id}</td>
                  <td className="py-3 px-4 whitespace-nowrap">
                    <TruncateWithTooltip text={user.name} />
                  </td>
                  <td className="py-3 px-4 whitespace-nowrap">{user.role}</td>
                  <td className="py-3 px-4 whitespace-nowrap">
                    <TruncateWithTooltip text={user.email} />
                  </td>
                  <td className="py-3 px-4 whitespace-nowrap">
                    {user.registered}
                  </td>
                  <td className="py-3 px-4 whitespace-nowrap">
                    <span
                      className={`font-medium ${
                        user.status === "Active"
                          ? "text-[var(--button-light-green-color)]"
                          : "text-[var(--primary-red-color)]"
                      }`}
                    >
                      {user.status}
                    </span>
                  </td>
                  <td className="py-3 px-4 flex  gap-2">
                    <button
                      className="w-[80px] h-[35px] border border-[var(--button-green-color)] text-[var(--button-green-color)] rounded-full text-xs flex items-center justify-center gap-1 flex-none"
                      onClick={() => handleView(user.id)}
                    >
                      <Eye size={14} /> View
                    </button>
                    <button
                      className="w-[80px] h-[35px] border border-[var(--button-purple-color)] text-[var(--button-purple-color)] rounded-full text-xs flex items-center justify-center gap-1 flex-none"
                      onClick={() => handleEdit(user.id)}
                    >
                      <Edit size={14} /> Edit
                    </button>
                    {user.status === "Active" ? (
                      <button
                        className="w-[80px] h-[35px] border border-[var(--primary-red-color)] text-[var(--primary-red-color)] rounded-full text-xs flex items-center justify-center gap-1 flex-none"
                        onClick={() => handleDelete(user.id)}
                        disabled={deletingUserId === user.id}
                      >
                        {deletingUserId === user.id ? (
                          <Loader2 size={16} className="animate-spin" />
                        ) : (
                          <Trash2 size={14} />
                        )}
                        Delete
                      </button>
                    ) : (
                      <button
                        className="w-[80px] h-[35px] border border-[var(--button-gray-color)] text-[var(--button-gray-color)] rounded-full text-xs flex items-center justify-center gap-1 flex-none"
                        onClick={() => handleRestore(user.id)}
                        disabled={restoringUserId === user.id}
                      >
                        {restoringUserId === user.id ? (
                          <Loader2 size={16} className="animate-spin" />
                        ) : (
                          <RotateCcw size={14} />
                        )}
                        Restore
                      </button>
                    )}
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="7" className="py-6 text-center text-gray-500">
                  No users found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Mobile Card View */}
      <div className="block md:hidden space-y-4">
        {paginatedUsers.length === 0 && (
          <div className="text-center text-gray-500 py-6">No users found.</div>
        )}
        {paginatedUsers.map((user, idx) => (
          <div
            key={idx}
            className="p-4 mx-2 border rounded-xl bg-white shadow-sm text-sm text-[var(--text-dark-gray-color)]"
          >
            <div>
              <span className="font-semibold">User ID:</span>
              {user.id}
            </div>
            <div>
              <span className="font-semibold">Full Name:</span>
              {user.name}
            </div>
            <div>
              <span className="font-semibold">Role:</span>
              {user.role}
            </div>
            <div>
              <span className="font-semibold">Email:</span>
              {user.email}
            </div>
            <div>
              <span className="font-semibold">Date Registered:</span>{" "}
              {user.registered}
            </div>
            <div>
              <span className="font-semibold">Status:</span>{" "}
              <span
                className={`font-medium ${
                  user.status === "Active"
                    ? "text-[var(--button-light-green-color)]"
                    : "text-[var(--primary-red-color)]"
                }`}
              >
                {user.status}
              </span>
            </div>
            <div className="flex gap-2 mt-2">
              <button
                onClick={() => handleView(user.id)}
                className="w-[80px] h-[35px] border border-[var(--button-green-color)] text-[var(--button-green-color)] rounded-full text-xs flex items-center justify-center gap-1 flex-none"
              >
                <Eye size={14} /> View
              </button>
              <button
                onClick={() => handleEdit(user.id)}
                className="w-[80px] h-[35px] border border-[var(--button-purple-color)] text-[var(--button-purple-color)] rounded-full text-xs flex items-center justify-center gap-1 flex-none"
              >
                <Edit size={14} /> Edit
              </button>
              {user.status === "Active" ? (
                <button
                  className="w-[80px] h-[35px] border border-[var(--primary-red-color)] text-[var(--primary-red-color)] rounded-full text-xs flex items-center justify-center gap-1 flex-none"
                  onClick={() => handleDelete(user.id)}
                  disabled={deletingUserId === user.id}
                >
                  {deletingUserId === user.id ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <Trash2 size={14} />
                  )}
                  Delete
                </button>
              ) : (
                <button
                  className="w-[80px] h-[35px] border border-[var(--button-gray-color)] text-[var(--button-gray-color)] rounded-full text-xs flex items-center justify-center gap-1 flex-none"
                  onClick={() => handleRestore(user.id)}
                  disabled={restoringUserId === user.id}
                >
                  {restoringUserId === user.id ? (
                    <Loader2 size={16} className="animate-spin" />
                  ) : (
                    <RotateCcw size={14} />
                  )}
                  Restore
                </button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Pagination */}
      <div className="hidden sm:flex justify-center items-center gap-2 py-4 flex-wrap">
        <button
          className="text-xs sm:text-sm text-gray-600"
          onClick={() => goToPage(currentPage - 1)}
          disabled={currentPage === 1}
        >
          &lt; Prev
        </button>

        {[...Array(totalPages)].map((_, i) => {
          const page = i + 1;
          return (
            <button
              key={page}
              onClick={() => goToPage(page)}
              className={`px-2 sm:px-3 py-1 border rounded-md text-xs sm:text-sm ${
                currentPage === page
                  ? "border-[var(--primary-red-color)] text-[var(--primary-red-color)]"
                  : "text-gray-700"
              }`}
            >
              {page}
            </button>
          );
        })}

        <button
          className="text-xs sm:text-sm text-[var(--text-gray-color)]"
          onClick={() => goToPage(currentPage + 1)}
          disabled={currentPage === totalPages}
        >
          Next &gt;
        </button>
      </div>
    </div>
  );
};

export default ViewUsers;
